/* -------------------------- Design imports start -------------------------- */
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import ChatMessage from './ChatMessage'
import LogTool from '../../../../logger/logTools'
import { useChatContext } from '../../utils/context'
import { type Message } from '../../utils/types'
import { useChannel } from '../../hooks/useChatQueries'
import { useUpdateMessage, useDeleteMessage } from '../../hooks/useChatMutations'
import { User } from '../../../../utils/types'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  verticalSpacing?: number
  sx?: React.CSSProperties
}

/**
 * Component that holds the conversation of a channel
 * Sender messages on the right side (editable)
 * Receiver messages on the left side (not editable)
 */

/* -------------------------------------------------------------------------- */
/*                               Component start                              */
/* -------------------------------------------------------------------------- */
export default function ChatConversation(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const { sx = {} } = props
  const { messages, newMessages, activeChannel, activeUser } = useChatContext()
  const log = new LogTool({ context: 'ChatConversation', enable: true, logLevel: 'warn' })
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- React Query Hooks --------------------------- */
  const channelId = activeChannel?.key
  const { data: channelData } = useChannel(channelId || '', ['memberships'], Boolean(channelId))
  const updateMessageMutation = useUpdateMessage()
  const deleteMessageMutation = useDeleteMessage()
  /* ---------------------------- React Query Hooks End ----------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const [showMessageActions, setShowMessageActions] = useState<number>(-1)
  const [lastVisited, setLastVisited] = useState<any>(undefined)
  const { t } = useTranslation(['chat', 'common'])
  const chatContainerRef = React.useRef<HTMLDivElement | null>(null)
  /* ----------------------------- Data states end ---------------------------- */
  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    scrollToBottom()
  }, [messages, newMessages])

  useEffect(() => {
    // Update lastVisited when channelData changes
    if (channelData && channelData.members && activeUser) {
      const member: User | null | undefined = Array.isArray(channelData.members)
        ? channelData.members.find((m: User) => m.self === activeUser.self)
        : null
      if (member && 'lastReadAt' in member && member.lastReadAt) {
        setLastVisited(member.lastReadAt)
      }
    }
  }, [channelData, activeUser])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  log.debug('lastVisited: ', lastVisited)

  async function onMessageDelete(message: Message): Promise<void> {
    try {
      await deleteMessageMutation.mutateAsync({
        messageSelf: message.self,
        deletedText: t('chat:content.label.messageDeleted'),
      })
      toast.success(t('common:feedback.success.deleteMessageSuccess'))
    } catch (error) {
      log.error('Error deleting message: ', error)
      toast.error(t('common:feedback.error.deleteMessageError'))
    }
  }

  async function onMessageEdit(message: any, editValue: string, edit: boolean, setEdit: any): Promise<void> {
    setEdit(!edit)
    if (edit) {
      try {
        await updateMessageMutation.mutateAsync({
          messageSelf: message.self,
          messageData: { body: editValue },
        })
        toast.success(t('common:feedback.success.editMessageSuccess'))
      } catch (error) {
        log.error('Error editing message: ', error)
        toast.error(t('common:feedback.error.editMessageError'))
      }
    }
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------- Render constants starts ------------------------ */
  const messageDivider = (): React.ReactNode => {
    return (
      /* show divider with date of that the channel was last visited */
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          color: '#BDBDBD',
        }}
      >
        <hr style={{ width: '20%' }} />
        <span>
          {t('chat:content.label.lastVisited')} {new Date(lastVisited).toLocaleString()}
        </span>
        <hr style={{ width: '20%' }} />
      </div>
    )
  }

  const scrollToBottom = (): void => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }

  const chatContainerStyle: React.CSSProperties = {
    ...sx,
    display: 'flex',
    flexDirection: 'column',
    padding: '16px',
    gap: '8px',
    margin: 'auto',
    overflowY: 'scroll',
  }

  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <div ref={chatContainerRef} style={chatContainerStyle}>
      {activeChannel?.self &&
        messages[activeChannel.self]?.map((message: any, index: number) => {
          const received: boolean =
            message.author.self && !message.author.self.includes(activeUser.id)
          return (
            <>
              <ChatMessage
                key={index}
                messageIndex={index}
                onMessageEdit={onMessageEdit}
                onMessageDelete={onMessageDelete}
                message={message}
                setShowMessageActions={setShowMessageActions}
                showMessageActions={showMessageActions}
                received={received}
                cornerRadius={8}
              />
            </>
          )
        })}
      {activeChannel?.self &&
        newMessages[activeChannel.self]?.length > 0 &&
        newMessages[activeChannel.self]?.filter((m: any) => m.author.self === activeUser.self)
          .length === 0 &&
        messageDivider()}
      {activeChannel?.self &&
        newMessages[activeChannel.self]?.length > 0 &&
        newMessages[activeChannel.self]
          ?.filter((m: any) => m.channel === activeChannel.self)
          .map((message: any, index: number) => {
            const received: boolean =
              message.author.self && !message.author.self.includes(activeUser.id)
            const newMessageIndex: number = messages[activeChannel.self]?.length + index
            return (
              <>
                <ChatMessage
                  key={newMessageIndex}
                  messageIndex={newMessageIndex}
                  onMessageEdit={onMessageEdit}
                  onMessageDelete={onMessageDelete}
                  message={message}
                  setShowMessageActions={setShowMessageActions}
                  showMessageActions={showMessageActions}
                  received={received}
                  cornerRadius={8}
                />
              </>
            )
          })}
    </div>
  )
}
